// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import { FormControl } from 'react-bootstrap';
import _ from 'lodash';
import { DURATION_TIME_UNITS, DurationTimeUnit, UnitWithLabel } from '@/main/app.constants';
import { FormError } from '@/hybrid/core/FormError.atom';
import { IconSelect } from '@/hybrid/core/IconSelect.molecule';
import classNames from 'classnames';

export type ValueWithUnitsItem = { value: number; units: string };

interface ValueWithUnitsProps {
  onChange: (ValueWithUnitsItem, property: string) => void;
  min: number;
  /** Classnames for `<IconSelect />` */
  selectClassNames?: string;
  defaultValue: ValueWithUnitsItem;
  propName?: string;
  isValid?: boolean;
  minIsExclusive?: boolean;
  required?: boolean;
  availableUnits?: readonly (DurationTimeUnit | UnitWithLabel)[];
  unitOptionIndex?: number;
  insideModal?: boolean;
  customError?: boolean;
  disabled?: boolean;
  fromFormBuilder?: boolean;
  largeValueInput?: boolean;
  step?: number | string;
  appendToBody?: boolean;
  extraClassNames?: string;
  fullWidth?: boolean;
}

export const ValueWithUnits: React.FunctionComponent<ValueWithUnitsProps> = (props) => {
  const {
    propName,
    onChange,
    defaultValue,
    min,
    minIsExclusive,
    required,
    availableUnits,
    insideModal,
    customError,
    disabled = false,
    largeValueInput = false,
    step = 'any',
    isValid,
    unitOptionIndex,
    selectClassNames,
    fromFormBuilder = false,
    appendToBody = true,
    extraClassNames,
    fullWidth = false,
  } = props;

  const [selectedUnit, setSelectedUnit] = useState({});
  const [selectedValue, setSelectedValue] = useState('');
  const [valid, setValid] = useState(true);
  const [displayOptions, setDisplayOptions] = useState([]);
  const [pristine, setPristine] = useState(true);

  useEffect(() => {
    const availableOptions = availableUnits || DURATION_TIME_UNITS;
    const updatedOptions = _.map(availableOptions, (option: any) => ({
      value: _.isNil(unitOptionIndex) ? option.unit[0] : option.unit[unitOptionIndex],
      text: option.translationKey ? option.translationKey : option.shortLabel,
    }));
    setDisplayOptions(updatedOptions);
    if (defaultValue) {
      // sometimes the backend returns a different unit than we send along when we create a condition so we need to
      // do some extra work to ensure we select the correct unit
      let unit = _.find(updatedOptions, { value: defaultValue.units as any });
      if (!unit) {
        _.forEach(availableOptions, (option) => {
          if (_.includes(option.unit, defaultValue.units)) {
            unit = _.find(updatedOptions, { value: option.unit[0] as any });
          }
        });
      }
      setSelectedUnit(unit);
      setSelectedValue(_.trim(defaultValue?.value?.toString()) ?? '');
    }
  }, [defaultValue, availableUnits]);

  const validate = ({ value, unit }) => {
    if (required || !_.isNil(unit)) {
      if (!_.isNil(min) || !_.isNil(min)) {
        return minIsExclusive ? value > min : value >= min;
      } else {
        return true;
      }
    }

    return true;
  };

  const triggerChange = (newValue, newUnit) => {
    let unit = selectedUnit;
    let value = selectedValue;
    setPristine(false);

    if (!_.isNaN(newValue) && !_.isNil(newValue)) {
      setSelectedValue(newValue);
      value = newValue;
    }

    if (!_.isNil(newUnit)) {
      setSelectedUnit(newUnit);
      unit = newUnit;
    }

    if (_.isFunction(onChange)) {
      const isValid = validate({ value, unit });
      setValid(isValid);

      onChange(
        _.assign(
          {},
          {
            value: value !== '' ? _.toNumber(value) : '',
            units: (unit as any)?.value,
            valid: isValid,
            propName,
          },
        ),
      );
    }
  };

  const renderErrorMessage = () => {
    if (pristine || customError || fromFormBuilder) {
      return null;
    } else if (required && (_.isNaN(selectedValue) || _.isNil(selectedUnit))) {
      return <FormError errorText="FORM.REQUIRED_FIELD" extraClassNames="help-block" />;
    } else if (!valid) {
      return (
        <FormError
          extraClassNames="help-block"
          errorText={minIsExclusive ? 'VALUE_WITH_UNITS.INVALID_MIN_EXCLUSIVE' : 'VALUE_WITH_UNITS.INVALID'}
          errorParameters={{ min }}
        />
      );
    }
  };

  return (
    <>
      <div
        className={classNames('flexColumnContainer specValueWithUnits', extraClassNames, { flexFill: fullWidth })}
        data-testid={propName}>
        <FormControl
          className={classNames(
            'value-with-units-value-input',
            'height-30',
            { 'error-border': _.isNil(isValid) ? !valid : !isValid },
            { flexFill: largeValueInput },
          )}
          data-testid="valueWithUnitsInput"
          name="value"
          placeholder=""
          type="number"
          step={step}
          value={selectedValue}
          onChange={(e) => {
            e.stopPropagation();
            setSelectedValue(e.target.value);
            triggerChange(e.target.value, null);
          }}
          disabled={disabled}
        />
        <div className="width-100">
          <IconSelect
            disabled={disabled}
            name="valueWithUnitsUnits"
            extraClassNames={classNames('value-with-units', selectClassNames)}
            value={selectedUnit}
            appendToBody={appendToBody}
            selectOptions={displayOptions}
            onChange={(val) => triggerChange(null, val)}
            insideModal={insideModal}
            skipMemo={true}
          />
        </div>
      </div>
      {renderErrorMessage()}
    </>
  );
};
export default ValueWithUnits;
